import i18n from 'i18next';

import type { Kpi } from '../types';

/**
 * The backend builds KPI labels and deltas in English. They are a small, fixed vocabulary, so the
 * client maps them to the active language; anything unknown is shown as returned.
 */
const LABELS: Record<string, string> = {
  'Active tasks': 'activeTasks',
  'Completed this sprint': 'completedSprint',
  'Hours closed (7d)': 'hours7d',
  Overdue: 'overdue',
  'Tasks closed': 'tasksClosed',
  'Avg cycle time': 'cycleTime',
  Rework: 'rework',
  'Delivery / plan': 'delivery',
  'Estimate reliability': 'reliability'
};

const DELTAS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^(\d+) due today$/, (m) => i18n.t('insights.kpi.dueToday', { count: Number(m[1]) })],
  [/^(\d+%) of sprint$/, (m) => i18n.t('insights.kpi.ofSprint', { value: m[1] })],
  [/^(\S+) vs prev\. week$/, (m) => i18n.t('insights.kpi.vsPrevWeek', { value: m[1] })],
  [/^(\S+) vs prev\. (\d+)w$/, (m) => i18n.t('insights.kpi.vsPrevWeeks', { value: m[1], weeks: m[2] })],
  [/^(\S+) vs prev\.$/, (m) => i18n.t('insights.kpi.vsPrev', { value: m[1] })],
  [/^([+-]?\d+) pts$/, (m) => i18n.t('insights.kpi.pts', { value: m[1] })],
  [/^All on time$/, () => i18n.t('insights.kpi.allOnTime')],
  [/^Needs attention$/, () => i18n.t('insights.kpi.needsAttention')],
  [/^Target (.+)$/, (m) => i18n.t('insights.kpi.target', { value: m[1] })],
  [/^within ±20%$/, () => i18n.t('insights.kpi.within')]
];

export function kpiLabel(label: string): string {
  const key = LABELS[label];
  return key ? i18n.t(`insights.kpi.${key}` as never) : label;
}

export function kpiDelta(delta: string): string {
  for (const [re, fmt] of DELTAS) {
    const m = delta.match(re);
    if (m) return fmt(m);
  }
  return delta;
}

export function localizeKpi(k: Kpi): Kpi {
  return { ...k, label: kpiLabel(k.label), ...(k.delta ? { delta: kpiDelta(k.delta) } : {}) };
}

/** Rework origin code → label, falling back to the code. */
export function reworkLabel(origin: string): string {
  const key = `insights.rework.${origin}`;
  return i18n.exists(key) ? i18n.t(key as never) : origin;
}
